import React, { useState, useEffect } from 'react';
import { Users, Crown, Shield, User, Eye, UserCheck, UserX, MoreVertical, Loader, AlertTriangle } from 'lucide-react';
import { userService } from '../../lib/userService';
import { useAuth } from '../../hooks/useAuth';
import { AppUser, UserRole } from '../../types';

export const AdminUserManagement: React.FC = () => {
  const { appUser, permissions } = useAuth();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingUser, setUpdatingUser] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (permissions.canViewUsers) {
      loadUsers();
    }
  }, [permissions.canViewUsers]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const fetchedUsers = await userService.getAllUsers();
      setUsers(fetchedUsers);
    } catch (error) {
      console.error('Failed to load users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    if (!permissions.canManageUsers || !appUser) return;

    // Prevent changing owner role
    const targetUser = users.find(u => u.id === userId);
    if (targetUser?.role === 'owner') {
      setError('Cannot change owner role');
      return;
    }

    // Prevent non-owners from creating other owners
    if (newRole === 'owner' && appUser.role !== 'owner') {
      setError('Only owners can assign owner role');
      return;
    }

    try {
      setUpdatingUser(userId);
      setError('');
      await userService.updateUserRole(userId, newRole, appUser.email);
      await loadUsers(); // Refresh list
    } catch (error: any) {
      setError(error.message || 'Failed to update user role');
    } finally {
      setUpdatingUser(null);
    }
  };

  const handleToggleActive = async (userId: string, isActive: boolean) => {
    if (!permissions.canManageUsers || !appUser) return;

    // Prevent deactivating owner
    const targetUser = users.find(u => u.id === userId);
    if (targetUser?.role === 'owner') {
      setError('Cannot deactivate owner account');
      return;
    }

    try {
      setUpdatingUser(userId);
      setError('');
      
      if (isActive) {
        await userService.reactivateUser(userId, appUser.email);
      } else {
        await userService.deactivateUser(userId, appUser.email);
      }
      
      await loadUsers(); // Refresh list
    } catch (error: any) {
      setError(error.message || 'Failed to update user status');
    } finally {
      setUpdatingUser(null);
    }
  };

  const getRoleIcon = (role: UserRole) => {
    switch (role) {
      case 'owner':
        return <Crown size={16} className="text-yellow-600" />;
      case 'admin':
        return <Shield size={16} className="text-blue-600" />;
      case 'user':
        return <User size={16} className="text-green-600" />;
      case 'view-only':
        return <Eye size={16} className="text-gray-600" />;
      default:
        return <User size={16} className="text-gray-600" />;
    }
  };

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case 'owner':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'admin':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'user':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'view-only':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getAvailableRoles = (currentUserRole: UserRole): UserRole[] => {
    if (appUser?.role === 'owner') {
      return ['owner', 'admin', 'user', 'view-only'];
    } else if (appUser?.role === 'admin') {
      // Admins can't assign owner role
      return ['admin', 'user', 'view-only'];
    }
    return [];
  };

  if (!permissions.canViewUsers) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-16 w-16 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
        <p className="text-gray-600">You don't have permission to view user management.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin" size={32} />
        <span className="ml-2">Loading users...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Users size={28} className="text-primary" />
            User Management
          </h2>
          <p className="text-gray-600 mt-1">Manage user roles and permissions</p>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-500">Total Users</div>
          <div className="text-2xl font-bold text-primary">{users.length}</div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">All Users</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                {permissions.canManageUsers && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                          {user.displayName?.charAt(0)?.toUpperCase() || user.email.charAt(0).toUpperCase()}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.displayName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getRoleIcon(user.role)}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                        {user.role}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {user.isActive ? (
                        <div className="flex items-center text-green-600">
                          <UserCheck size={16} className="mr-1" />
                          <span className="text-sm">Active</span>
                        </div>
                      ) : (
                        <div className="flex items-center text-red-600">
                          <UserX size={16} className="mr-1" />
                          <span className="text-sm">Inactive</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(user.createdAt)}
                  </td>
                  
                  {permissions.canManageUsers && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {/* Role Change Dropdown */}
                        {user.role !== 'owner' && (
                          <select
                            value={user.role}
                            onChange={(e) => handleRoleChange(user.id, e.target.value as UserRole)}
                            disabled={updatingUser === user.id}
                            className="text-sm border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                          >
                            {getAvailableRoles(user.role).map(role => (
                              <option key={role} value={role}>
                                {role}
                              </option>
                            ))}
                          </select>
                        )}

                        {/* Active/Inactive Toggle */}
                        {user.role !== 'owner' && (
                          <button
                            onClick={() => handleToggleActive(user.id, !user.isActive)}
                            disabled={updatingUser === user.id}
                            className={`px-3 py-1 rounded text-xs font-medium disabled:opacity-50 ${
                              user.isActive
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                          >
                            {updatingUser === user.id ? (
                              <Loader className="animate-spin" size={12} />
                            ) : (
                              user.isActive ? 'Deactivate' : 'Activate'
                            )}
                          </button>
                        )}

                        {user.role === 'owner' && (
                          <span className="text-xs text-gray-400 italic">Protected</span>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {['owner', 'admin', 'user', 'view-only'].map((role) => {
          const count = users.filter(u => u.role === role).length;
          return (
            <div key={role} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  {getRoleIcon(role as UserRole)}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500 capitalize">{role}</p>
                  <p className="text-2xl font-semibold text-gray-900">{count}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};